import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.tsx';
import { Flame, Mail, Lock, ArrowRight, AlertCircle } from 'lucide-react';
import { animate, stagger } from 'animejs';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const formRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Boot-up animation sequence
        if (formRef.current) {
            animate('.login-element', {
                translateY: [30, 0],
                opacity: [0, 1],
                duration: 800,
                ease: 'outExpo',
                delay: stagger(100, { start: 200 }),
            });
        }
    }, []);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password);
            navigate('/dashboard');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Login failed');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div
            className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden blueprint-grid"
            style={{ background: 'var(--dark-bg)' }}
        >
            {/* Ambient Fire Glow */}
            <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                <div
                    className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 animate-float"
                    style={{ background: 'var(--fire-red)' }}
                />
                <div
                    className="absolute bottom-1/4 right-1/4 w-72 h-72 rounded-full blur-3xl opacity-15 animate-float"
                    style={{ background: 'var(--fire-orange)', animationDelay: '1s' }}
                />
            </div>

            {/* Radar Pulse */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
                <div
                    className="w-64 h-64 rounded-full border-2 opacity-20"
                    style={{
                        borderColor: 'var(--fire-orange)',
                        animation: 'radarPulse 3s cubic-bezier(0.16, 1, 0.3, 1) infinite'
                    }}
                />
            </div>

            {/* Technical Status Labels */}
            <div className="absolute top-8 right-8 tech-label opacity-70 hidden md:block" aria-hidden="true">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                SECURE CONNECTION
            </div>
            <div className="absolute bottom-8 left-8 tech-label opacity-70 hidden md:block" aria-hidden="true">
                AUTH_SYS v2.4.1
            </div>

            <div ref={formRef} className="w-full max-w-md relative z-10">
                {/* Logo & Brand */}
                <div className="text-center mb-8 login-element opacity-0">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 animate-float"
                        style={{
                            background: 'linear-gradient(135deg, var(--fire-red), var(--fire-orange))',
                            boxShadow: '0 0 40px rgba(255, 107, 53, 0.4)'
                        }}
                    >
                        <Flame className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">
                        Welcome Back
                    </h1>
                    <p className="text-gray-400">
                        Access your compliance dashboard
                    </p>
                </div>

                {/* Login Card */}
                <div className="card-terminal p-8 login-element opacity-0">
                    {/* Terminal Header */}
                    <div className="flex items-center gap-2 mb-6 pb-4 border-b border-[var(--blueprint-line)]">
                        <div className="flex gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-500/80" />
                            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                            <div className="w-3 h-3 rounded-full bg-green-500/80" />
                        </div>
                        <span className="text-xs text-gray-500 font-mono ml-2">LOGIN_TERMINAL</span>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Error Alert */}
                        {error && (
                            <div className="alert-terminal">
                                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                <p className="alert-terminal-text">{error}</p>
                            </div>
                        )}

                        {/* Email Field */}
                        <div className="login-element opacity-0">
                            <label htmlFor="email" className="label-terminal">
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-terminal pl-12"
                                    placeholder="you@company.com"
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div className="login-element opacity-0">
                            <label htmlFor="password" className="label-terminal">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input-terminal pl-12"
                                    placeholder="••••••••"
                                    required
                                    autoComplete="current-password"
                                />
                            </div>
                        </div>

                        {/* Remember & Forgot */}
                        <div className="flex items-center justify-between text-sm login-element opacity-0">
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 rounded border-gray-600 bg-transparent text-[var(--fire-orange)] focus:ring-2 focus:ring-[var(--fire-orange)]/50 focus:ring-offset-0 focus:ring-offset-transparent"
                                />
                                <span className="text-gray-400 group-hover:text-gray-300 transition-colors">
                                    Remember me
                                </span>
                            </label>
                            <Link
                                to="/forgot-password"
                                className="text-[var(--fire-orange)] hover:text-white font-medium transition-colors"
                            >
                                Forgot password?
                            </Link>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-terminal-primary w-full flex items-center justify-center gap-2 login-element opacity-0"
                        >
                            {loading ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    <span>Authenticating...</span>
                                </>
                            ) : (
                                <>
                                    <span>Sign In</span>
                                    <ArrowRight className="w-5 h-5" />
                                </>
                            )}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className="relative my-8 login-element opacity-0">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-[var(--blueprint-line)]" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-4 bg-[#111] text-gray-500">
                                New to the platform?
                            </span>
                        </div>
                    </div>

                    {/* Register Link */}
                    <Link
                        to="/register"
                        className="btn-terminal-secondary w-full text-center block login-element opacity-0"
                    >
                        Create an Account
                    </Link>
                </div>

                {/* Footer */}
                <p className="text-center text-sm text-gray-600 mt-8 login-element opacity-0">
                    © 2026 Environaise. Built with care for engineers.
                </p>
            </div>
        </div>
    );
}
